import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Loader2, RotateCcw, ChevronDown, ChevronUp, History, User, Clock, AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { adminMarketLogs, adminMarketUndo, apiErr } from "@/lib/api";
import { formatPrice } from "@/lib/site";

const OP_META = {
  bulk_pct_up: { label: "% Zam", cls: "bg-red-50 text-red-700 border-red-200" },
  bulk_pct_down: { label: "% İndirim", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  bulk_tl_up: { label: "+₺ Artış", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  bulk_tl_down: { label: "−₺ Azalış", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  bulk_set_active: { label: "Satışa Açıldı", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  bulk_set_passive: { label: "Pasife Alındı", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  inline: { label: "Satır İçi", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  mixed: { label: "Karışık", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  undo: { label: "Geri Alma", cls: "bg-purple-50 text-purple-700 border-purple-200" },
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      timeZone: "Europe/Istanbul", day: "2-digit", month: "short",
      year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const ItemRow = ({ it }) => {
  const priceChanged = it.old_price != null && it.new_price != null && it.old_price !== it.new_price;
  const statusChanged = it.old_status && it.new_status && it.old_status !== it.new_status;
  return (
    <div className="flex items-center justify-between gap-2 border-b border-gray-50 py-1 text-xs last:border-0">
      <span className="min-w-0 flex-1 truncate text-gray-600">{it.name}</span>
      <div className="flex shrink-0 items-center gap-2 font-mono">
        {priceChanged && (
          <span>
            <span className="text-gray-400 line-through">{formatPrice(it.old_price)}</span>
            <span className="mx-1 text-[#C57B43]">→</span>
            <span className="font-semibold text-[#9a5e30]">{formatPrice(it.new_price)}</span>
          </span>
        )}
        {statusChanged && (
          <span className={it.new_status === "passive" ? "text-gray-400" : "text-emerald-600"}>
            {it.new_status === "passive" ? "Pasif" : "Satışta"}
          </span>
        )}
      </div>
    </div>
  );
};

export default function MarketPriceHistory() {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState({});
  const [undoTarget, setUndoTarget] = useState(null);
  const [undoing, setUndoing] = useState(false);

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["admin", "market-logs"],
    queryFn: adminMarketLogs,
  });

  const doUndo = async () => {
    if (!undoTarget) return;
    setUndoing(true);
    try {
      const res = await adminMarketUndo(undoTarget.id);
      toast.success(`${res.restored} ürün eski fiyatına döndürüldü`);
      setUndoTarget(null);
      qc.invalidateQueries({ queryKey: ["admin", "market-logs"] });
      qc.invalidateQueries({ queryKey: ["admin", "market"] });
      qc.invalidateQueries({ queryKey: ["market-products"] });
      qc.invalidateQueries({ queryKey: ["market-groups"] });
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setUndoing(false);
    }
  };

  return (
    <div data-testid="market-price-history">
      <div className="mb-4 flex items-center gap-2">
        <History className="h-5 w-5 text-[#C57B43]" />
        <div>
          <h3 className="text-lg font-semibold">Fiyat Geçmişi</h3>
          <p className="text-sm text-gray-500">Tüm market fiyat/durum değişiklikleri kayıt altında. Yanlış işlemi tek tıkla geri alın.</p>
        </div>
      </div>

      {isLoading ? (
        <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-gray-400" /></div>
      ) : logs.length === 0 ? (
        <div className="rounded-lg border border-dashed border-gray-200 py-16 text-center text-sm text-gray-400" data-testid="market-history-empty">
          Henüz fiyat değişikliği yapılmadı. "Market Fiyat" sekmesinden bir değişiklik kaydedince burada görünür.
        </div>
      ) : (
        <div className="space-y-2.5">
          {logs.map((log) => {
            const meta = OP_META[log.op_type] || OP_META.inline;
            const isOpen = !!expanded[log.id];
            const canUndo = !log.undone && log.op_type !== "undo";
            return (
              <div
                key={log.id}
                data-testid={`market-log-${log.id}`}
                className={`rounded-lg border p-3.5 ${log.undone ? "border-gray-200 bg-gray-50 opacity-75" : "border-gray-200 bg-white"}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className={`rounded border px-2 py-0.5 text-[11px] font-medium ${meta.cls}`}>{meta.label}</span>
                      {log.undone && (
                        <span className="rounded border border-gray-200 bg-gray-100 px-2 py-0.5 text-[11px] text-gray-500" data-testid={`market-log-undone-${log.id}`}>
                          Geri Alındı
                        </span>
                      )}
                      <span className="text-[11px] text-gray-400">· {log.affected} ürün</span>
                    </div>
                    <p className="text-sm font-medium text-gray-800">{log.summary}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-gray-400">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(log.created_at)}</span>
                      <span className="flex items-center gap-1"><User className="h-3 w-3" /> {log.user}</span>
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-1.5">
                    <Button
                      variant="ghost"
                      size="sm"
                      data-testid={`market-log-detail-${log.id}`}
                      onClick={() => setExpanded((e) => ({ ...e, [log.id]: !e[log.id] }))}
                      className="h-8 text-xs text-gray-500"
                    >
                      Detay ({log.affected})
                      {isOpen ? <ChevronUp className="ml-1 h-3.5 w-3.5" /> : <ChevronDown className="ml-1 h-3.5 w-3.5" />}
                    </Button>
                    {canUndo && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`market-log-undo-${log.id}`}
                        onClick={() => setUndoTarget(log)}
                        className="h-8 border-[#C57B43]/50 text-xs text-[#9a5e30] hover:bg-[#C57B43]/10"
                      >
                        <RotateCcw className="mr-1 h-3.5 w-3.5" /> Geri Al
                      </Button>
                    )}
                  </div>
                </div>
                {isOpen && (
                  <div className="mt-3 max-h-64 overflow-y-auto rounded border border-gray-100 bg-gray-50/50 px-3 py-1.5" data-testid={`market-log-items-${log.id}`}>
                    {log.items.map((it) => <ItemRow key={it.slug} it={it} />)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!undoTarget} onOpenChange={(o) => !o && setUndoTarget(null)}>
        <DialogContent data-testid="market-undo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> İşlemi Geri Al
            </DialogTitle>
            <DialogDescription>
              {undoTarget && (
                <>Bu işlem <b>{undoTarget.affected}</b> ürünün fiyatını/durumunu önceki haline döndürecek.<br />
                <span className="mt-1 block text-xs text-gray-400">"{undoTarget.summary}"</span></>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUndoTarget(null)} disabled={undoing} data-testid="market-undo-cancel">Vazgeç</Button>
            <Button onClick={doUndo} disabled={undoing} data-testid="market-undo-confirm" className="bg-[#C57B43] hover:bg-[#b06d39]">
              {undoing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1.5 h-4 w-4" />}
              Evet, Geri Al
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
